import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import WeKitLogo from "@/components/WeKitLogo";
import { Copy, IndianRupee, Link2, Loader2, ShieldCheck, Wallet } from "lucide-react";
import {
  Affiliate,
  AffiliateConversion,
  buildAffiliateUrl,
  enrollAffiliate,
  fetchMyAffiliate,
  fetchMyConversions,
} from "@/lib/affiliate";
import { toast } from "sonner";

const statusBadge = (status: Affiliate["status"]) => {
  if (status === "approved") return <Badge className="bg-primary text-primary-foreground">Approved</Badge>;
  if (status === "pending") return <Badge variant="secondary">Pending review</Badge>;
  return <Badge variant="destructive">Suspended</Badge>;
};

const convStatusLabel: Record<AffiliateConversion["status"], string> = {
  pending: "Pending",
  approved: "Approved",
  paid: "Paid",
  reversed: "Reversed",
};

const AffiliatePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [affiliate, setAffiliate] = useState<Affiliate | null>(null);
  const [conversions, setConversions] = useState<AffiliateConversion[]>([]);
  const [payoutEmail, setPayoutEmail] = useState("");

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      const aff = await fetchMyAffiliate(user.id);
      setAffiliate(aff);
      setPayoutEmail(aff?.payout_email ?? user.email ?? "");
      if (aff) {
        const c = await fetchMyConversions(aff.id);
        setConversions(c);
      }
      setLoading(false);
    })();
  }, [user]);

  const link = useMemo(() => (affiliate ? buildAffiliateUrl(affiliate.code) : ""), [affiliate]);

  const totals = useMemo(() => {
    const pendingAmt = conversions
      .filter((c) => c.status === "pending" || c.status === "approved")
      .reduce((s, c) => s + Number(c.commission_amount_inr || 0), 0);
    const paidAmt = conversions
      .filter((c) => c.status === "paid")
      .reduce((s, c) => s + Number(c.commission_amount_inr || 0), 0);
    return { pending: pendingAmt, paid: paidAmt, count: conversions.length };
  }, [conversions]);

  const handleEnroll = async () => {
    setSubmitting(true);
    try {
      const aff = await enrollAffiliate(payoutEmail || user?.email || "");
      setAffiliate(aff);
      toast.success("You're enrolled — our team will approve shortly.");
    } catch (e: any) {
      toast.error(e?.message || "Could not enroll. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    toast.success("Link copied");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 bg-background/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <WeKitLogo size="sm" />
          <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" /> Affiliates</Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10 space-y-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">Creator Affiliate Program</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Mentors and creators earn a commission on every paid WeKIT™ Career Clarity 360 assessment unlocked through their link.
          </p>
        </div>

        {!affiliate ? (
          <Card className="border-primary/20">
            <CardHeader>
              <CardTitle>Become an affiliate</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-5">
                <li><span className="text-foreground font-medium">20% commission</span> on every successful paid conversion.</li>
                <li>Your own tracked link: <span className="font-mono">wekit.ai/a/AFF-YOURNAME</span>.</li>
                <li>Monthly UPI / bank payouts once approved.</li>
                <li>Transparent dashboard — every conversion logged in real time.</li>
              </ul>
              <Separator />
              <div className="space-y-2 max-w-md">
                <Label htmlFor="payout-email">Payout email</Label>
                <Input id="payout-email" type="email" value={payoutEmail} onChange={(e) => setPayoutEmail(e.target.value)} placeholder="you@email.com" />
                <p className="text-xs text-muted-foreground">We'll contact you here for KYC and payout details.</p>
              </div>
              <Button onClick={handleEnroll} disabled={submitting} className="gap-2">
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Apply to join
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> Lifetime earned</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <IndianRupee className="h-5 w-5 text-foreground" />
                    <span className="text-3xl font-bold text-foreground">{Math.round(Number(affiliate.total_earned_inr) || 0).toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Pending payout</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <IndianRupee className="h-5 w-5 text-foreground" />
                    <span className="text-3xl font-bold text-foreground">{Math.round(totals.pending).toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Conversions</div>
                  <div className="mt-2 text-3xl font-bold text-foreground">{totals.count}</div>
                  <div className="text-xs text-muted-foreground mt-1">at {Number(affiliate.commission_pct).toFixed(0)}% commission</div>
                </CardContent>
              </Card>
            </div>

            {/* Link */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Link2 className="h-4 w-4" /> Your affiliate link</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>{statusBadge(affiliate.status)}</div>
                  <span className="text-xs text-muted-foreground font-mono">{affiliate.code}</span>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input readOnly value={link} className="font-mono text-sm" />
                  <Button variant="outline" onClick={copyLink} className="gap-2">
                    <Copy className="h-4 w-4" /> Copy
                  </Button>
                </div>
                {affiliate.status !== "approved" && (
                  <p className="text-xs text-muted-foreground">
                    Your link works once approved. We typically review within 2 business days.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Conversions table */}
            <Card>
              <CardHeader>
                <CardTitle>Recent conversions</CardTitle>
              </CardHeader>
              <CardContent>
                {conversions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No conversions yet. Share your link to start earning.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border/60">
                          <th className="py-2 pr-3">Date</th>
                          <th className="py-2 pr-3">Gross</th>
                          <th className="py-2 pr-3">Rate</th>
                          <th className="py-2 pr-3">Commission</th>
                          <th className="py-2 pr-3">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conversions.map((c) => (
                          <tr key={c.id} className="border-b border-border/40 last:border-0">
                            <td className="py-2 pr-3 text-foreground">{new Date(c.created_at).toLocaleDateString()}</td>
                            <td className="py-2 pr-3 text-foreground">₹{Number(c.gross_amount_inr).toLocaleString()}</td>
                            <td className="py-2 pr-3 text-muted-foreground">{Number(c.commission_pct).toFixed(0)}%</td>
                            <td className="py-2 pr-3 font-semibold text-foreground">₹{Number(c.commission_amount_inr).toLocaleString()}</td>
                            <td className="py-2 pr-3">
                              <Badge variant={c.status === "paid" ? "default" : "secondary"}>{convStatusLabel[c.status]}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default AffiliatePage;
